import React, { useMemo } from 'react';
import { Form, Input, FormInstance, Select } from 'antd';
import styled from 'styled-components';
import { PackageFormValues } from '.';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import colorTheme from 'constants/color';
import Text from 'components/Text';
import Button from 'components/Button';
import Add from 'assets/Icons/add';
import PackageTierCard from './PackageTierCard';
import { PACKAGE_TYPE } from 'features/settingService/services/constants';
import { ChevronDown } from 'lucide-react';

const { TextArea } = Input;


const validityOptions = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '24 months' },
];


export const calculateTier = (
  price: number,
  quantity: number,
  discountType: string,
  discountValue: number,
) => {
  const basePrice = Number(price || 0);
  const qty = Number(quantity || 0);
  const discount = Number(discountValue || 0);

  const originalPrice = basePrice * qty;

  let discountAmount = 0;

  if (discountType === 'PERCENT') {
    discountAmount = (originalPrice * discount) / 100;
  } else {
    discountAmount = discount;
  }

  discountAmount = Math.max(0, Math.min(discountAmount, originalPrice));

  const packagePrice = originalPrice - discountAmount;
  const pricePerSession = qty > 0 ? packagePrice / qty : 0;

  const savings = originalPrice - packagePrice;

  return {
    packagePrice,
    pricePerSession,
    savings,
  };
};

interface PackageFormProps {
  form: FormInstance<PackageFormValues>;
  initialValues?: any;
  onSubmit: (values: any) => void;
  isEdit?: boolean;
}

const PackageForm = ({
  form,
  initialValues,
  onSubmit,
  isEdit,
}: PackageFormProps) => {
  const price = Form.useWatch('price', form) || 0;
  const tiers = Form.useWatch('tiers', form) || [];

  const tierCalculations = useMemo(() => {
    return tiers.map((tier) => {
      return calculateTier(
        price,
        tier?.quantity,
        tier?.discountType,
        tier?.discountValue,
      );
    });
  }, [price, tiers]);

  const handleFinish = (values: PackageFormValues) => {
    const payload = {
      name: values.name?.trim(),
      description: values.description || '',
      price: Number(values.price || 0),
      validityMonths: Number(values.validityMonths || 0),

      tiers: (values.tiers || []).map((tier, index) => ({
         ...(isEdit ? { id: tier.id } : {}),
        name: tier.name?.trim(),
        quantity: Number(tier.quantity || 0),
        discountType: tier.discountType || 'PERCENT',
        discountValue: Number(tier.discountValue || 0),
        type: tier.type || PACKAGE_TYPE.BRONZE,
        sortOrder: index + 1,
      })),
    };

    onSubmit?.(payload);
  };

  return (
    <StyledForm
      form={form}
      layout='vertical'
      initialValues={{
        price: 0,
        validityMonths: 3,
        tiers: [
          {
            name: '',
            quantity: 1,
            discountType: 'PERCENT',
            discountValue: 0,
            type: PACKAGE_TYPE.BRONZE,
          },
        ],
        ...initialValues,
      }}
      onFinish={handleFinish}
    >
      <InfoSection>
        <SectionHeader>
          <StepNumber>1</StepNumber>

          <div>
            <Text variant='H5'>Basic Information</Text>
            <Text variant='CONTENT_1' color='text_3_v2'>
              Enter the package information
            </Text>
          </div>
        </SectionHeader>

        <InfoContent>
          <Form.Item
            name='name'
            label='Package Name'
            rules={[
              {
                required: true,
                message: 'Please enter package name',
              },
            ]}
          >
            <Input placeholder='Please enter...' />
          </Form.Item>

          <Form.Item
            name='price'
            label='Price'
            rules={[
              {
                required: true,
                message: 'Please enter price',
              },
              {
                type: 'number',
                min: 0,
                message: 'Price must be greater than or equal to 0',
              },
            ]}
          >
            <CurrencyInputPrecision size='medium' />
          </Form.Item>
          <Form.Item
            name='validityMonths'
            label='Validity Period (month)'
            rules={[
              {
                required: true,
                message: 'Please enter validity period',
              },
            ]}
          >
            <Select suffixIcon={<ChevronDown />} options={validityOptions} popupClassName="reward-select-dropdown"/>
          </Form.Item>
        </InfoContent>

        <Form.Item name='description' label='Description'>
          <TextArea rows={3} placeholder='Please enter...' style={{ height: 'auto' }} />
        </Form.Item>
      </InfoSection>

      <PackageSection>
        <SectionHeader>
          <StepNumber>2</StepNumber>

          <div>
            <Text variant='H5'>Package Options (Tiers)</Text>
            <Text variant='CONTENT_1' color='text_3_v2'>
              Set sessions and discount. Prices update automatically.
            </Text>
          </div>
        </SectionHeader>

        <Form.List name='tiers'>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => {
                const calculation = tierCalculations[index] || {
                  packagePrice: 0,
                  pricePerSession: 0,
                  savings: 0,
                };

                return (
                    <PackageTierCard
                        key={field.key}
                        field={field}
                        remove={remove}
                        calculation={calculation}
                    />
                );
              })}

              <Button
                ntype='NEW_TYPE'
                onClick={() =>
                  add({
                    name: '',
                    quantity: 1,
                    discountType: 'PERCENT',
                    discountValue: 0,
                    type: PACKAGE_TYPE.BRONZE,
                  })
                }
                iconElement={
                  <Add
                    fill={colorTheme.v2_secondary_3}
                    width='40'
                    height='40'
                  />
                }
                style={{ height: '4rem' }}
              >
                Add Tier
              </Button>
            </>
          )}
        </Form.List>
      </PackageSection>
    </StyledForm>
  );
};

export default PackageForm;

const StyledForm = styled(Form<PackageFormValues>)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: Poppins;
`;

const InfoSection = styled.div`
  padding: 16px;
  background: #fff;
  border-radius: 8px;

  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-input {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
    height: 3.5rem;
    border-color: ${colorTheme.v2_neutral_5};
  }

  .ant-form-item-label > label {
    font-weight: 400;
    font-size: 20px;
    font-family: Poppins;
  }

  .ant-input-number-input {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
    height: 3.5rem;
    border-color: ${colorTheme.v2_neutral_5};
  }
`;

const PackageSection = styled.div`
  padding: 16px;
  background: #fff;
  border-radius: 8px;

  .CONTENT_2 {
    font-size: 20px;
    font-family: Poppins;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const InfoContent = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  .ant-select-selection-item {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
  }
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #253247;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
`;